import classNames from 'classnames';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import CardSelector from './CardSelector';
import ProgramContentSelector from './ProgramContentSelector';
import Recipients from './Recipients';
import FilterCheckbox from '../filters/FilterCheckbox';
import { LoadingSpinner } from '../helpers/LoadingSpinner';
import localeNames from '../../utils/localeNames.jsx.erb';
import { canSendMessage } from '../../utils/communication/validationUtils';

/**
 * CommunicationMessageModal - Presentational component for composing and sending messages
 * This component is fully controlled via props and config, with no Redux dependencies
 */
const CommunicationMessageModal = ({
  config,
  initialMessage,
  initialCards,
  initialProgramContentEntries,
  initialRecipients,
  preselectedPlayer,
  filtersForGroups,
  filtersForGroupsReady,
  isSending,
  isTranslating,
  isDetecting,
  translation,
  onSend,
  onCancel,
  onDetectLanguage,
  onTranslate,
  onError,
  onModalClassChange,
}) => {
  // Local state for form management
  const [ages, setAges] = useState([]);
  const [forIndividuals, setForIndividuals] = useState(config.defaultMode === 'individual');
  const [contentAvailable, setContentAvailable] = useState(true);
  const [cards, setCards] = useState(initialCards || []);
  const [programContentEntries, setProgramContentEntries] = useState(initialProgramContentEntries || []);
  const [aiTranslate, setAiTranslate] = useState(false);
  const [messageBody, setMessageBody] = useState(initialMessage || '');
  const [recipients, setRecipients] = useState(initialRecipients || []);
  const [previewMode, setPreviewMode] = useState(config.forcePreview);
  const [translations, setTranslations] = useState([]);
  const [isEditingLanguage, setIsEditingLanguage] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Initialize translations data
  useEffect(() => {
    const data = Object.entries(localeNames).map(([k, v]) => ({
      value: k,
      label: v,
    }));
    setTranslations(data);

    // Set up translate-only mode if player is preselected
    if (preselectedPlayer) {
      setMessageBody(initialMessage || '');
      setRecipients([{
        value: preselectedPlayer.playerId,
        label: preselectedPlayer.playerName,
        accountName: preselectedPlayer.accountName
      }]);
    }
  }, []);

  // Handle preview mode and trigger translation
  useEffect(() => {
    onModalClassChange?.({ 'o-communication__modal--preview': previewMode });
    if (previewMode && aiTranslate) {
      onDetectLanguage({ text: messageBody });
      onTranslate({ text: messageBody });
    }
  }, [previewMode]);

  // Update translations when translation data arrives
  useEffect(() => {
    if (isEmpty(translation?.translated)) {
      return;
    }
    const updatedTranslations = translations.map(t => {
      // Do not update the message for the original language
      if (t.value === translation?.detected?.code) {
        return { ...t, message: messageBody };
      }

      const translatedText = translation.translated.find(m => m.language === t.value)?.text;
      return { ...t, message: translatedText };
    });
    setTranslations(updatedTranslations);
  }, [translation?.translated]);

  const handleSendMessage = () => {
    if (isSending) {
      return;
    }

    onSend({
      messageBody,
      recipients,
      forIndividuals,
      ages,
      cards,
      programContentEntries,
      contentAvailable,
      translations,
    });
  };

  const updateTranslation = (value, message) => {
    const updatedTranslations = translations.map(t => {
      if (t.value === value) {
        return { ...t, message: message };
      }
      return t;
    });
    setTranslations(updatedTranslations);
  };

  const handleCancelClick = () => {
    if (previewMode && !config.forcePreview) {
      return setPreviewMode(false);
    }
    onCancel();
  };

  const handleRecipientsMode = (individual) => {
    onModalClassChange?.({ 'o-communication__modal--wide': !individual });
    setForIndividuals(individual);
    setRecipients([]); // reset already selected recipients
  };

  const handleSendPreviewClick = () => {
    if (aiTranslate && !previewMode) {
      setPreviewMode(true);
    } else {
      handleSendMessage();
    }
  };

  const renderRecipients = () => {
    return (
      <>
        {config.allowModeSwitch && (
          <div className="row u-margin-top--base u-margin-bottom--tiny">
            <div className="col-6">
              <input
                type="radio"
                checked={forIndividuals}
                onChange={() => handleRecipientsMode(true)}
              />
              <label onClick={() => handleRecipientsMode(true)}>
                {config.labels.individualModeLabel}
              </label>
            </div>
            <div className="col-6">
              <input
                type="radio"
                checked={!forIndividuals}
                onChange={() => handleRecipientsMode(false)}
              />
              <label onClick={() => handleRecipientsMode(false)}>
                {config.labels.groupModeLabel}
              </label>
            </div>
          </div>
        )}
        <Recipients
          filters={{ ...filtersForGroups }}
          selectedIndividuals={recipients}
          isLoadingGroups={!filtersForGroupsReady}
          forGroups={!forIndividuals}
          onAgeChange={setAges}
          onRecipientsChange={setRecipients}
          onErrorMessage={onError}
          individualPlaceholder={config.labels.individualPlaceholder}
          groupPlaceholder={config.labels.groupPlaceholder}
        />
      </>
    );
  };

  const renderMessageForm = () => {
    return (
      <>
        <div className="u-padding-bottom--tiny">
          <label>Message body:</label>
          <textarea
            className="u-padding-bottom--zero u-margin-bottom--zero"
            rows="5"
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            disabled={isSending}
          />
        </div>
        {config.allowTranslation && (
          <FilterCheckbox
            label="Translate"
            value={aiTranslate}
            onChange={(value) => setAiTranslate(value)}
            disabled={isSending}
          />
        )}
      </>
    );
  };

  const renderOptional = () => {
    const cardSelectorDisabled = config.showCardSelector === false;
    const tipSelectorDisabled = config.showTipSelector === false;
    const hasContent = !isEmpty(cards) || !isEmpty(programContentEntries);

    return (
      <>
        <h2 className="u-padding-top--base u-txt--base u-txt--bold">Add content</h2>
        <CardSelector
          value={cards}
          onChange={(value) => setCards(value)}
          isDisabled={isSending || !isEmpty(programContentEntries) || !config.allowContentSelection || cardSelectorDisabled}
          onErrorMessage={onError}
          className="u-margin-bottom--tiny"
        />
        <ProgramContentSelector
          value={programContentEntries}
          onChange={(value) => setProgramContentEntries(value)}
          isDisabled={isSending || !isEmpty(cards) || !config.allowContentSelection || tipSelectorDisabled}
          onErrorMessage={onError}
          placeholder={config.labels.pcPlaceholder}
        />
        <FilterCheckbox
          labelCss="margin-right-0"
          label="Make content available for those that don't have access"
          value={contentAvailable}
          onChange={(value) => setContentAvailable(value)}
          disabled={isSending}
        />
      </>
    );
  };

  const renderTargets = () => {
    const targets = recipients.map(recipient => {
      if (forIndividuals) {
        return {
          label: recipient.label,
          value: recipient.value,
          accountName: recipient.accountName,
        };
      } else {
        return {
          label: recipient.label,
          value: recipient.value,
        };
      }
    });

    const individuals = (recipient, idx) => {
      return (
        <span key={recipient.value}>
          {recipient.accountName} - caregiver of {recipient.label}
          {idx < targets.length - 1 ? ', ' : ''}
        </span>
      );
    };

    const groups = (recipient, idx) => {
      return (
        <span key={recipient.value}>
          {recipient.label}
          {idx < targets.length - 1 ? ', ' : ''}
        </span>
      );
    };

    return (
      <div>
        To:&nbsp;
        {targets.map((recipient, idx) => {
          return forIndividuals ? individuals(recipient, idx) : groups(recipient, idx);
        })}
      </div>
    );
  };

  const renderMessage = () => {
    return <div className="u-margin-top--base">{messageBody}</div>;
  };

  const renderTranslationsBlock = () => {
    const renderTranslationMessage = (translation) => {
      if (isEditingLanguage !== translation.value) {
        return <div>{translation.message}</div>;
      }

      return (
        <textarea
          className="u-padding-bottom--zero u-margin-bottom--zero u-margin-top--small"
          rows="5"
          value={translation.message || ''}
          onChange={(e) => updateTranslation(translation.value, e.target.value)}
          disabled={isSending}
        />
      );
    };

    const renderButtons = (language) => {
      if (isEditingLanguage === language) {
        return (
          <button
            className="u-margin-bottom--zero o-sp-button o-sp-button--profile o-communication__button--edit"
            onClick={() => {
              setIsEditingLanguage('');
              setIsEditing(false);
            }}
          >
            <i className="fa fa-check" aria-hidden="true"></i>
            <span className="u-margin-left--tiny">Save</span>
          </button>
        );
      } else {
        return (
          <button
            className={classNames('u-margin-bottom--zero o-sp-button o-sp-button--profile o-communication__button--edit', {
              'o-communication__button--disabled': isEditing,
            })}
            disabled={isEditing}
            onClick={() => {
              setIsEditingLanguage(language);
              setIsEditing(true);
            }}
          >
            <i className="fa fa-pencil" aria-hidden="true"></i>
            <span className="u-margin-left--tiny">Edit</span>
          </button>
        );
      }
    };

    const canEdit = (language) => {
      if (isDetecting || isTranslating) {
        return false;
      }
      return language !== translation.detected?.code;
    };

    return (
      <div style={{ overflowY: 'auto', height: '300px' }}>
        <div className="u-txt--label u-padding-bottom--small text-justify u-txt">
          We used OpenAI GPT API to suggest translations for your message. The family will receive
          the message in the language they use on Sparkler. Please review/edit these translations
          as you see fit.
        </div>
        <h2 className="u-txt--base u-txt--bold">Translations</h2>
        {translations.map((t, idx) => {
          return (
            <div key={idx} className="u-padding-bottom--rem">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div
                  className="u-padding-bottom--small u-txt--small-xs"
                  style={{ fontWeight: '600' }}
                >
                  {t.label}
                </div>
                {canEdit(t.value) ? renderButtons(t.value) : ''}
              </div>
              {isTranslating ? <LoadingSpinner /> : renderTranslationMessage(t)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderControls = () => {
    const buttonTitle = () => {
      if (isSending) {
        return 'Sending...';
      } else if (previewMode) {
        return 'Send';
      } else {
        return aiTranslate ? 'Preview' : 'Send';
      }
    };

    const canSend = canSendMessage({
      messageBody,
      recipients,
      isSending,
      isDetecting,
      isTranslating,
    });

    return (
      <div className="u-padding-top--base">
        <button
          className={classNames('o-sp-button o-sp-button--update u-float--left u-margin-right--base', {
            'o-communication__button--disabled': !canSend,
            'o-sp-button--primary': canSend,
          })}
          disabled={!canSend}
          onClick={handleSendPreviewClick}
        >
          {buttonTitle()}
        </button>
        <button className="o-sp-button cs-txt-link--sp-blue" onClick={handleCancelClick}>
          Cancel
        </button>
      </div>
    );
  };

  return (
    <div className="column small-12">
      <h1 className="u-txt--header">
        <b>{previewMode ? config.labels.previewTitle : config.labels.modalTitle}</b>
      </h1>
      {!previewMode && (
        <>
          {renderRecipients()}
          {renderMessageForm()}
          {renderOptional()}
        </>
      )}
      {previewMode && (
        <div className="u-margin-top--base u-margin-bottom--tiny">
          {renderTargets()}
          {renderMessage()}
          {renderOptional()}
          <hr />
          {renderTranslationsBlock()}
        </div>
      )}
      {renderControls()}
    </div>
  );
};

CommunicationMessageModal.propTypes = {
  // Configuration
  config: PropTypes.shape({
    allowModeSwitch: PropTypes.bool,
    allowTranslation: PropTypes.bool,
    allowContentSelection: PropTypes.bool,
    forcePreview: PropTypes.bool,
    showAgeRanges: PropTypes.bool,
    defaultMode: PropTypes.oneOf(['individual', 'group']),
    showCardSelector: PropTypes.bool,
    showTipSelector: PropTypes.bool,
    labels: PropTypes.shape({
      individualModeLabel: PropTypes.string,
      groupModeLabel: PropTypes.string,
      individualPlaceholder: PropTypes.string,
      groupPlaceholder: PropTypes.string,
      pcPlaceholder: PropTypes.string,
      modalTitle: PropTypes.string,
      previewTitle: PropTypes.string,
    }),
  }).isRequired,

  // Initial values
  initialMessage: PropTypes.string,
  initialCards: PropTypes.array,
  initialProgramContentEntries: PropTypes.array,
  initialRecipients: PropTypes.array,
  preselectedPlayer: PropTypes.shape({
    playerId: PropTypes.number,
    playerName: PropTypes.string,
    accountName: PropTypes.string,
  }),

  // State from parent/Redux
  filtersForGroups: PropTypes.shape({
    ages: PropTypes.array,
    cohorts: PropTypes.array,
    groups: PropTypes.array,
    subgroups: PropTypes.array,
  }).isRequired,
  filtersForGroupsReady: PropTypes.bool.isRequired,
  isSending: PropTypes.bool,
  isTranslating: PropTypes.bool,
  isDetecting: PropTypes.bool,
  translation: PropTypes.shape({
    detected: PropTypes.object,
    translated: PropTypes.array,
  }),

  // Callbacks
  onSend: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDetectLanguage: PropTypes.func.isRequired,
  onTranslate: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onModalClassChange: PropTypes.func,
};

CommunicationMessageModal.defaultProps = {
  initialMessage: '',
  initialCards: [],
  initialProgramContentEntries: [],
  initialRecipients: [],
  preselectedPlayer: null,
  isSending: false,
  isTranslating: false,
  isDetecting: false,
  translation: {},
};

export default CommunicationMessageModal;
